/**
 * AJ2 · Buy box — comportamiento de los bloques aj2-* de la ficha.
 *
 * 1. Badge: la pastilla morada vive SOBRE la foto grande de la galería.
 *    El editor no permite meter hijos en `_product-media-gallery` (bloque
 *    privado), así que el bloque se coloca en la columna de compra y aquí
 *    se recoloca dentro de `slideshow-container` (el visor) — NUNCA en
 *    `.product-information__media` a secas: con las miniaturas a la
 *    izquierda caería sobre la rail de thumbnails. Sin JavaScript se queda
 *    como pastilla al principio de la columna: cambia de sitio, no
 *    desaparece.
 * 2. Selector de tratamiento: elegir el pack navega a su ficha.
 * 3. Completa la rutina: casillas, botón y nota del envío gratis que se
 *    recalculan con lo marcado y la variante, y añadir de una vez (/cart/add.js).
 * 4. Precio, coste por unidad y tramo de Klarna: se recalculan al cambiar
 *    de variante.
 */

/* ---------- 1 · Badge sobre el visor de la galería ---------- */
function colocarBadge() {
  const badge = document.querySelector('[data-aj2-badge]');
  if (!badge) return;
  const visor = document.querySelector('.product-information__media slideshow-container');
  if (visor && !visor.contains(badge)) {
    visor.prepend(badge);
  }
}

/* ---------- 1a · El marco REAL de la foto ---------- */
/**
 * El visor (`slideshow-container`) ocupa siempre todo el ancho de la
 * columna, pero la foto no: Horizon le pone `.constrain-height`, así que
 * en ventanas bajas la media encoge en ancho (mantiene su ratio) y queda
 * centrada, dejando franjas a los lados. El badge y las flechas cuelgan
 * del visor, así que se quedaban FUERA de la foto (Aaron, 17/09).
 *
 * Aquí se publica el ancho real de la media en `--aj2-media-w` y el CSS
 * mete badge y flechas dentro de ese marco, con cualquier resolución y
 * cualquier ratio de imagen. Medimos `.product-media-constraint-wrapper`,
 * que es justo la caja de la media; si Horizon lo cambiara, se cae al
 * `img`/`video` del slide activo.
 */
function medirMarcoMedia() {
  const visor = document.querySelector('.product-information__media slideshow-container');
  if (!visor) return;
  const activo =
    visor.querySelector('slideshow-slide[aria-hidden="false"]') || visor.querySelector('slideshow-slide');
  const marco =
    activo?.querySelector('.product-media-constraint-wrapper') || activo?.querySelector('img, video');
  if (!marco) return;
  const ancho = Math.round(marco.getBoundingClientRect().width);
  /* Solo se escribe si cambia: escribir en cada tick del observer sería
     trabajo de estilo gratis en cada scroll. */
  if (ancho > 0 && visor.style.getPropertyValue('--aj2-media-w') !== `${ancho}px`) {
    visor.style.setProperty('--aj2-media-w', `${ancho}px`);
  }
}

function vigilarMarcoMedia() {
  const visor = document.querySelector('.product-information__media slideshow-container');
  if (!visor || visor.dataset.aj2Marco) return;
  visor.dataset.aj2Marco = '1';

  let pendiente = false;
  const remedir = () => {
    if (pendiente) return;
    pendiente = true;
    window.requestAnimationFrame(() => {
      pendiente = false;
      medirMarcoMedia();
    });
  };

  /* Cambio de tamaño del visor (ventana, zoom, sticky) y cambio de slide
     (Horizon mueve el aria-hidden de un slide a otro). */
  new ResizeObserver(remedir).observe(visor);
  new MutationObserver(remedir).observe(visor, {
    attributes: true,
    attributeFilter: ['aria-hidden'],
    subtree: true,
  });
  /* Las imágenes tardan en decidir su caja hasta que cargan. */
  for (const media of visor.querySelectorAll('img')) {
    if (!media.complete) media.addEventListener('load', remedir, { once: true });
  }
  /* En una pestaña de fondo el navegador congela rAF y los observers: al
     volver a primer plano hay que medir otra vez, no fiarse del último
     valor escrito antes de ocultarse. */
  window.addEventListener('resize', remedir);
  document.addEventListener('visibilitychange', remedir);
  medirMarcoMedia();
}

/* ---------- 1b · Estudio bajo la galería ---------- */
function colocarEstudio() {
  const estudio = document.querySelector('[data-aj2-estudio]');
  if (!estudio) return;
  const media = document.querySelector('.product-information__media');
  const visor = media?.querySelector('slideshow-container');
  /* Guardián EXACTO contra el bucle con el MutationObserver: after() mueve
     el nodo aunque ya esté en su sitio, y cada movimiento re-dispara al
     observer. Solo se mueve si NO es ya el hermano siguiente del visor. */
  if (media && visor && visor.nextElementSibling !== estudio) {
    visor.after(estudio);
  }
}

/* ---------- 1c · Comparador antes/después (modo mitad y mitad) ---------- */
function iniciarComparadores() {
  for (const caja of document.querySelectorAll('[data-aj2-comparador]')) {
    if (caja.dataset.aj2Listo) continue;
    caja.dataset.aj2Listo = '1';
    const rango = caja.querySelector('[data-aj2-comparador-rango]');
    rango?.addEventListener('input', () => {
      caja.style.setProperty('--aj2-corte', `${rango.value}%`);
    });
  }
}

/* ---------- 2 · Selector de tratamiento ---------- */
function iniciarSelector() {
  for (const opcion of document.querySelectorAll('[data-aj2-pack-url]')) {
    if (opcion.dataset.aj2Listo) continue;
    opcion.dataset.aj2Listo = '1';
    opcion.addEventListener('click', () => {
      opcion.setAttribute('aria-pressed', 'true');
      window.location.href = opcion.dataset.aj2PackUrl;
    });
  }
}

/* ---------- Variante elegida (compartido por 3 y 4) ----------
 *
 * DE DÓNDE SALE LA VARIANTE (corregido el 26/09/2026). Antes se leía el
 * PRIMER input marcado del selector y se repintaba al `change` y a los
 * 200 ms. Con una sola opción funcionaba; con varias (Tamaño · Color ·
 * Cuántos tarros) el primer input marcado es el de Tamaño, y su
 * `data-variant-id` no cambia hasta que el tema recibe el HTML nuevo del
 * servidor. Si esa respuesta tardaba más de 200 ms, el precio se quedaba en
 * la variante anterior: con «1 tarro» elegido seguía diciendo €71,98 (Aaron,
 * 26/09).
 *
 * Ahora la variante sale del JSON que el propio selector lleva dentro
 * (`variant-picker script[type="application/json"]`), y quien lo necesite se
 * apunta con `alCambiarVariante`: un único MutationObserver vigila la
 * sección, solo reacciona a cambios dentro de `variant-picker` y avisa a
 * todos. Se agenda con setTimeout y no con rAF, que en una pestaña de fondo
 * se congela.
 */
function varianteElegidaDatos() {
  const script = document.querySelector('.product-information variant-picker script[type="application/json"]');
  if (!script) return null;
  try {
    const variante = JSON.parse(script.textContent);
    return variante && variante.id ? variante : null;
  } catch (error) {
    return null; /* JSON a medio morfar */
  }
}

function varianteElegida() {
  const variante = varianteElegidaDatos();
  if (variante) return String(variante.id);
  const oculto = document.querySelector('.product-information form[action*="/cart/add"] [name="id"]');
  return oculto && oculto.value ? String(oculto.value) : null;
}

const oyentesVariante = new Set();
let vigilandoVariante = false;

function alCambiarVariante(oyente) {
  oyentesVariante.add(oyente);
  if (vigilandoVariante) return;
  vigilandoVariante = true;

  const selector = document.querySelector('.product-information variant-picker');
  const raizSeccion = (selector && selector.closest('.shopify-section')) || document.body;
  const tocaSelector = (registro) => {
    const nodo = registro.target.nodeType === 1 ? registro.target : registro.target.parentElement;
    if (nodo && nodo.closest('variant-picker')) return true;
    return Array.from(registro.addedNodes).some(
      (n) => n.nodeType === 1 && (n.matches('variant-picker') || n.querySelector('variant-picker'))
    );
  };
  let pendiente = false;
  new MutationObserver((registros) => {
    if (pendiente || !registros.some(tocaSelector)) return;
    pendiente = true;
    window.setTimeout(() => {
      pendiente = false;
      for (const fn of oyentesVariante) fn();
    }, 0);
  }).observe(raizSeccion, { childList: true, subtree: true, characterData: true });
}

/* ---------- 3 · Completa la rutina ----------
 *
 * BOTÓN Y NOTA DINÁMICOS (26/09/2026). El botón decía «Añadir los dos» con
 * un solo complemento marcado, y la nota del envío gratis era un texto fijo
 * («Con cualquiera de los dos superas los 40 €») que mentía con las fibras
 * de 2,5 gr (7,99 € + 11,99 € = 19,98 €) y sobraba con 3 tarros (71,98 €).
 *
 * Ahora:
 *   - Botón: `data-boton-dos` con dos marcados, `data-boton-uno` (con
 *     [producto]) con uno, `data-boton-ninguno` y desactivado con ninguno.
 *   - Nota: base = precio de la variante elegida × cantidad; se suma lo
 *     marcado y se compara con `data-umbral` (céntimos). Si no hay nada
 *     marcado, dice qué complemento haría falta. Lo que ya haya en el
 *     carrito no cuenta: el mensaje habla de este pedido.
 * Los textos vienen del bloque (ajustes editables), aquí solo se eligen y se
 * rellenan [umbral], [importe] y [producto].
 */
function formatearDinero(centimos) {
  const muestra = document.querySelector('[data-aj2-crossell-total]');
  const coma = (centimos / 100).toFixed(2).replace('.', ',');
  if (muestra && muestra.textContent && muestra.textContent.trim().startsWith('€')) {
    return `€${coma}`;
  }
  return `${coma} €`;
}

function cantidadElegida() {
  const campo = document.querySelector('.product-information form[action*="/cart/add"] [name="quantity"]');
  const n = campo ? parseInt(campo.value, 10) : 1;
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function iniciarCrossell() {
  const caja = document.querySelector('[data-aj2-crossell]');
  if (!caja || caja.dataset.aj2Listo) return;
  caja.dataset.aj2Listo = '1';

  const boton = caja.querySelector('[data-aj2-crossell-boton]');
  const texto = caja.querySelector('[data-aj2-crossell-texto]');
  const importe = caja.querySelector('[data-aj2-crossell-importe]');
  const total = caja.querySelector('[data-aj2-crossell-total]');
  const nota = caja.querySelector('[data-aj2-crossell-nota]');
  const items = () => [...caja.querySelectorAll('.aj2-crossell__item')];
  const d = caja.dataset;
  const umbral = Number(d.umbral || 0);

  const rellenar = (plantilla, datos) =>
    (plantilla || '')
      .replace(/\[umbral\]/g, d.umbralTexto || '')
      .replace(/\[importe\]/g, datos.importe || '')
      .replace(/\[producto\]/g, datos.producto || '');

  const precioBase = () => {
    const variante = varianteElegidaDatos();
    const unidad = variante && typeof variante.price === 'number' ? variante.price : Number(d.base || 0);
    return unidad * cantidadElegida();
  };

  const textoNota = (activos) => {
    const base = precioBase();
    if (base >= umbral) return rellenar(d.notaGratis, {});

    /* Sin nada marcado se evalúan todos los complementos como candidatos. */
    const todos = items();
    const grupo = activos.length ? activos : todos;
    const precio = (i) => Number(i.dataset.precio || 0);
    const suma = grupo.reduce((acc, i) => acc + precio(i), 0);

    if (base + suma < umbral) {
      return rellenar(d.notaFalta, { importe: formatearDinero(umbral - base - (activos.length ? suma : 0)) });
    }
    const bastanSolos = grupo.filter((i) => base + precio(i) >= umbral);
    if (grupo.length === 2) {
      if (bastanSolos.length === 2) return rellenar(d.notaCualquiera, {});
      if (bastanSolos.length === 1 && !activos.length) {
        return rellenar(d.notaUno, { producto: bastanSolos[0].dataset.nombre });
      }
      return rellenar(d.notaLosDos, {});
    }
    return rellenar(d.notaUno, { producto: grupo[0].dataset.nombre });
  };

  const recalcular = () => {
    const activos = items().filter((i) => i.dataset.activo === 'true');
    const suma = activos.reduce((acc, i) => acc + Number(i.dataset.precio || 0), 0);
    if (total) total.textContent = formatearDinero(suma);

    if (texto) {
      if (!activos.length) texto.textContent = d.botonNinguno || '';
      else if (activos.length === 2) texto.textContent = d.botonDos || '';
      else texto.textContent = rellenar(d.botonUno, { producto: activos[0].dataset.nombre });
    }
    if (importe) importe.hidden = activos.length === 0;
    if (boton instanceof HTMLButtonElement) boton.disabled = activos.length === 0;

    if (nota && umbral > 0) nota.textContent = textoNota(activos);
  };

  for (const item of items()) {
    const check = item.querySelector('.aj2-crossell__check');
    check?.addEventListener('click', () => {
      const activo = item.dataset.activo === 'true';
      item.dataset.activo = activo ? 'false' : 'true';
      check.setAttribute('aria-pressed', String(!activo));
      recalcular();
    });
  }

  boton?.addEventListener('click', async () => {
    const activos = items().filter((i) => i.dataset.activo === 'true');
    if (!activos.length || !(boton instanceof HTMLButtonElement)) return;
    boton.disabled = true;
    try {
      const respuesta = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: activos.map((i) => ({ id: Number(i.dataset.variante), quantity: 1 })),
        }),
      });
      if (!respuesta.ok) throw new Error('cart/add');
      if (texto) texto.textContent = activos.length > 1 ? caja.dataset.anadidos || 'Añadidos al carrito ✓' : caja.dataset.anadido || 'Añadido al carrito ✓';
      if (importe) importe.hidden = true;
      window.setTimeout(() => window.location.reload(), 600);
    } catch (error) {
      boton.disabled = false;
    }
  });

  /* La nota depende del precio de la ficha: se recalcula al cambiar de
     variante y, si algún día se monta el selector de cantidad, al cambiarla. */
  alCambiarVariante(recalcular);
  document.addEventListener('change', (evento) => {
    if (evento.target instanceof HTMLInputElement && evento.target.name === 'quantity' && evento.target.closest('.product-information')) {
      recalcular();
    }
  });

  recalcular();
}

/* ---------- 4 · Precio, coste por unidad y Klarna al cambiar de variante ----------
 *
 * Se lee de los mapas que publican los bloques aj2-price y aj2-pago (importes
 * ya formateados por la tienda), no del product-price de Horizon: esta PDP no
 * lo monta. La variante, de `varianteElegida` (ver arriba).
 */
function leerMapa(nodo) {
  if (!nodo) return null;
  try {
    return JSON.parse(nodo.textContent);
  } catch (error) {
    return null;
  }
}

function iniciarPrecio() {
  const precio = document.querySelector('[data-aj2-price]');
  const pago = document.querySelector('[data-aj2-pago]');
  const ancla = precio || pago;
  if (!ancla || ancla.dataset.aj2Listo) return;
  ancla.dataset.aj2Listo = '1';

  const mapaPrecio = precio ? leerMapa(precio.querySelector('[data-aj2-price-mapa]')) : null;
  const mapaPago = pago ? leerMapa(pago.querySelector('[data-aj2-pago-mapa]')) : null;
  if (!mapaPrecio && !mapaPago) return;

  const actual = precio?.querySelector('[data-aj2-price-actual]');
  const unidad = precio?.querySelector('[data-aj2-price-unidad]');
  const tramo = pago?.querySelector('[data-aj2-pago-tramo]');

  let ultima = null;
  const pintar = () => {
    const id = varianteElegida();
    if (!id || id === ultima) return;
    ultima = id;
    const datos = mapaPrecio?.[id];
    if (datos) {
      if (actual && datos.precio) actual.textContent = datos.precio;
      if (unidad) unidad.textContent = datos.unidad || '';
    }
    if (tramo && mapaPago?.[id]) tramo.textContent = mapaPago[id];
  };

  alCambiarVariante(pintar);
  pintar();
}

function iniciar() {
  colocarBadge();
  vigilarMarcoMedia();
  colocarEstudio();
  iniciarComparadores();
  iniciarSelector();
  iniciarCrossell();
  iniciarPrecio();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', iniciar, { once: true });
} else {
  iniciar();
}

/* La galería de Horizon se sustituye entera al cambiar de variante: si el
   badge viaja con la galería vieja, se vuelve a colocar. */
const raiz = document.querySelector('[data-product-grid-content]');
if (raiz) {
  const vigia = new MutationObserver(() => {
    colocarBadge();
    vigilarMarcoMedia();
    colocarEstudio();
  });
  vigia.observe(raiz, { childList: true, subtree: true });
}